/**
 * useVariantStore — resolves the active VariantConfig from hostname +
 * route meta. App-scoped Pinia singleton; `variant` recomputes on route
 * changes. `window.location.hostname` is read once at initialization
 * (the SPA never sees a hostname change without a reload).
 *
 * DEV-only `?variant=<id>` URL override lets `npm run dev` cycle the
 * dpi + non-dpi UI without /etc/hosts entries. Override persists in
 * sessionStorage; clear with `?variant=auto`. Gated behind
 * `import.meta.env.DEV` so production NEVER honors a URL-claimed
 * variant — production stays hostname-driven + server-enforced via
 * `X-Forwarded-Host`.
 */

import { defineStore } from "pinia";
import { computed, type ComputedRef } from "vue";
import { useRoute } from "vue-router";

import {
    readVariantParam,
    resolveVariant,
    variantMetaFromParam,
    type VariantRouteMeta,
} from "@src/config/variants.ts";
import type { VariantConfig, VariantId } from "@src/types/variant.ts";

const DEV_VARIANT_KEY = "speedtest:dev-variant";

/**
 * Read the DEV-only variant override from URL query (`?variant=dpi|default|auto`)
 * with sessionStorage fallback. Returns `null` outside DEV builds, in SSR,
 * or when no override is set.
 */
function readDevVariantOverride(): VariantId | null {
    if (!import.meta.env.DEV) return null;
    if (typeof window === "undefined" || !window.location) return null;

    const params = new URLSearchParams(window.location.search);
    const claimed = params.get("variant");

    if (claimed === "auto") {
        // Explicit reset — clear the persisted override and fall through to
        // hostname-based resolution.
        sessionStorage.removeItem(DEV_VARIANT_KEY);
        return null;
    }
    if (claimed === "dpi" || claimed === "default") {
        sessionStorage.setItem(DEV_VARIANT_KEY, claimed);
        return claimed;
    }

    const stored = sessionStorage.getItem(DEV_VARIANT_KEY);
    if (stored === "dpi" || stored === "default") return stored;
    return null;
}

export const useVariantStore = defineStore("variant", () => {
    const route = useRoute();
    const hostname =
        typeof window !== "undefined" && window.location
            ? window.location.hostname
            : "";
    const devOverride = readDevVariantOverride();

    const variant: ComputedRef<VariantConfig> = computed(() =>
        resolveVariant({
            hostname,
            // DDR-AV-ROUTE: the four flat variant records collapsed to ONE
            // parameterized record (`/:variant(test|school|home)?`), so the
            // variant identity now lives in `route.params.variant`, not in a
            // per-record static `meta`. Derive the meta from the param; fall
            // back to `route.meta` so callers that stamp `meta.variantId`
            // directly (unit-test routers, future static records) still
            // resolve — keeping the registry's resolution order intact.
            routeMeta:
                variantMetaFromParam(readVariantParam(route.params)) ??
                (route.meta as VariantRouteMeta | undefined),
            override: devOverride,
        }),
    );

    return { variant };
});
